// 規則（RULE-01 / 02）：家戶的自動化與場景——列表（啟用開關、放在哪裡跑、上次結果 / 失敗原因、次數）、執行紀錄、
// 從範本建立（門磁開 → 錄影 + 通知、布防中 PIR → 警報器 + 通知、AI 有人 → 通知）、進階 JSON 編輯（存檔前伺服器驗證、問題逐條列出）。
// 放在本地或雲端由平台決定（參與設備都在同一台執行者底下就本地，斷雲照跑）；權限由伺服器驗。
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { KinioError } from '@kinio/web-sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useHomeLabel, useScope } from '../../lib/scope';
import { refFullName } from '../../lib/devices';
import { rulesApi, type Rule, type RuleProblem, type RuleSpec } from '../../lib/rulesApi';
import { buildTemplate, candidates, missingRole, type TemplateId } from './templates';
import { MoreMenu } from '../../components/MoreMenu';
import { stamp } from '../../lib/timefmt';

const TEMPLATES: TemplateId[] = ['door_record_notify', 'armed_pir_siren', 'ai_person_notify'];

/** 422 RULE_INVALID 的 detail.problems（SPEC #eng-10）。 */
function problemsOf(e: unknown): RuleProblem[] | undefined {
  return e instanceof KinioError ? (e.detail?.problems as RuleProblem[] | undefined) : undefined;
}

function errCode(e: unknown): string {
  const x = e as { code?: string; body?: { error?: string }; message?: string };
  return x.body?.error ?? x.code ?? x.message ?? String(e);
}

/** 交接中的規則清單重抓間隔 */
const HANDOFF_POLL_MS = 5_000;

export function RulesPage() {
  const { t } = useTranslation();
  const { visible } = useScope();
  const homeLabel = useHomeLabel();
  const homes = visible.filter((h) => !!h.role);
  const [homeId, setHomeId] = useState('');
  const home = homes.find((h) => h.home_id === homeId) ?? homes[0];
  if (!home) return <p className="card muted">{t('rules.no_home')}</p>;
  return (
    <section>
      <div className="toolbar wrap">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('nav.rules')}
        </h1>
        {homes.length > 1 && (
          <select className="input" style={{ width: 'auto' }} value={home.home_id} onChange={(e) => setHomeId(e.target.value)} aria-label={t('rules.home')}>
            {homes.map((h) => (
              <option key={h.home_id} value={h.home_id}>
                {homeLabel(h)}
              </option>
            ))}
          </select>
        )}
      </div>
      <HomeRules api={home.endpoints.api} homeId={home.home_id} canWrite={home.role !== 'guest'} />
    </section>
  );
}

function HomeRules({ api, homeId, canWrite }: { api: string; homeId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['rules', homeId],
    queryFn: () => rulesApi.list(api, homeId),
    retry: false,
    // 有規則在交接中：每 5 秒重抓，交接完成畫面自己更新（SPEC #automation-167；home-base#22 同款）
    refetchInterval: (q) => (q.state.data?.some((r) => r.status?.handoff) ? HANDOFF_POLL_MS : false),
  });
  const catalog = useQuery({ queryKey: ['rules-catalog', homeId], queryFn: () => rulesApi.catalog(api, homeId), retry: false, staleTime: 60_000 });
  const [tpl, setTpl] = useState<TemplateId | null>(null);
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);
  const [openRuns, setOpenRuns] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['rules', homeId] });
  const act = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setErr(errCode(e));
    }
  };
  const name = (device_ref?: string) =>
    device_ref ? refFullName(device_ref, catalog.data?.things.find((x) => x.device_ref === device_ref)?.display_name, catalog.data?.things ?? []) : '';
  // 交接中的說明（SPEC #automation-167「執行者交接」，plan#35）：舊的本地執行設備上線停用舊版後，新位置才執行
  const handoffText = (r: Rule) => {
    const h = r.status?.handoff;
    const from = h?.from ? name(h.from) : t('rules.handoff_old_device');
    if (h?.deleting) return t('rules.handoff_deleting', { from });
    if (!r.enabled && r.placement === 'local' && (!h?.from || h.from === r.executor_ref)) return t('rules.handoff_disabled', { from });
    const to = r.placement === 'local' && r.executor_ref ? name(r.executor_ref) : t('rules.handoff_cloud');
    return t('rules.handoff_pending', { from, to });
  };
  if (list.isError && !list.data) return <p className="card muted">{t('rules.unavailable')}</p>;
  return (
    <>
      {canWrite && (
        <div className="toolbar">
          <button className="btn btn-primary" onClick={() => setTpl('door_record_notify')} disabled={!catalog.data}>
            {t('rules.from_template')}
          </button>
          {/* JSON 編輯是進階功能：收在〔⋯〕裡（SPEC RULE-01 / 02，P1-11） */}
          <MoreMenu label={t('rules.more')} items={[{ key: 'json', label: t('rules.new_json'), onClick: () => setEditing('new') }]} />
        </div>
      )}
      {err && <p className="error small">{t(`rules.err_${err}`, { defaultValue: t('common.error') })}</p>}
      {list.isLoading && <p className="muted">{t('common.loading')}</p>}
      {list.data?.length === 0 && (
        // 空狀態直接放三張範本卡：一句說明規則能做什麼＋〔用這個範本〕；這個家戶沒有範本要的設備時寫出缺什麼
        <div data-testid="rules-empty">
          <p>{t('rules.empty_intro')}</p>
          <div className="rule-cards">
            {TEMPLATES.map((id) => {
              const missing = catalog.data?.things ? missingRole(id, catalog.data.things) : null;
              return (
                <div key={id} className="card rule-card" data-template={id}>
                  <strong>{t(`rules.tpl_${id}`)}</strong>
                  <p className="muted small">{t(`rules.tpl_${id}_hint`)}</p>
                  {missing && <p className="small warn-text">{t(`rules.tpl_${id}_needs`)}</p>}
                  {canWrite && (
                    <button className="btn btn-sm" disabled={!catalog.data || !!missing} onClick={() => setTpl(id)}>
                      {t('rules.use_template')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {canWrite && <p className="small muted">{t('rules.shared_devices_note')}</p>}
      <ul className="rule-list">
        {(list.data ?? []).map((r) => {
          const write = canWrite && !r.status?.handoff?.deleting; // 刪除中的墓碑：只顯示，不能改、不能執行
          return (
          <li key={r.rule_id} className="card rule-row" data-rule={r.rule_id}>
            <div className="toolbar wrap">
              <label className="check">
                <input
                  type="checkbox"
                  checked={r.enabled !== false}
                  disabled={!write}
                  aria-label={t('rules.enabled')}
                  onChange={(e) => void act(() => rulesApi.patch(api, homeId, r.rule_id, { enabled: e.target.checked }))}
                />
                <strong>{r.name}</strong>
              </label>
              <span className="badge">{t(`rules.kind_${r.kind}`)}</span>
              <span className="badge" title={r.executor_ref ? name(r.executor_ref) : undefined}>
                {t(`rules.placement_${r.placement}`)}
                {r.placement === 'local' && r.executor_ref ? ` · ${name(r.executor_ref)}` : ''}
              </span>
              {r.status?.pending_sync && <span className="badge badge-warn">{t('rules.pending_sync')}</span>}
              {r.status?.handoff && (
                <span className="badge badge-warn" data-testid="rule-handoff" title={stamp(r.status.handoff.since)}>
                  {handoffText(r)}
                </span>
              )}
              <span className="spacer" />
              <span className="small muted" data-testid="rule-status">
                {r.status?.last_result ? t(`rules.result_${r.status.last_result}`) : t('rules.never')}
                {r.status?.last_fired_at ? ` · ${stamp(r.status.last_fired_at)}` : ''}
                {r.status?.fires_total ? ` · ${t('rules.fires', { n: r.status.fires_total })}` : ''}
              </span>
              {write && (
                <button className="btn btn-sm" onClick={() => void act(() => rulesApi.run(api, homeId, r.rule_id))}>
                  {t('rules.run')}
                </button>
              )}
              <button className="btn btn-sm" aria-expanded={openRuns === r.rule_id} onClick={() => setOpenRuns(openRuns === r.rule_id ? null : r.rule_id)}>
                {t('rules.runs')}
              </button>
              {write && (
                <>
                  <button className="btn btn-sm" onClick={() => setEditing(r)}>
                    {t('rules.edit')}
                  </button>
                  <button
                    className="btn btn-sm"
                    aria-label={t('rules.delete')}
                    onClick={async () => (await ask({ danger: true, title: t('rules.delete_confirm', { name: r.name }), action: t('common.action_delete') })) && void act(() => rulesApi.remove(api, homeId, r.rule_id))}
                  >
                    🗑
                  </button>
                </>
              )}
            </div>
            {r.status?.last_result === 'failed' && r.status.last_error && (
              <p className="error small">
                {t('rules.last_error', {
                  reason: t(`rules.reason_${r.status.last_error.reason}`, { defaultValue: t('rules.reason_other') }),
                  n: (r.status.last_error.action_index ?? -1) + 1,
                })}
              </p>
            )}
            {openRuns === r.rule_id && <Runs api={api} homeId={homeId} rule={r} />}
          </li>
          );
        })}
      </ul>
      {tpl && catalog.data && (
        <TemplateDialog
          catalog={catalog.data}
          initial={tpl}
          onClose={() => setTpl(null)}
          onCreate={async (spec) => {
            await rulesApi.create(api, homeId, spec);
            setTpl(null);
            refresh();
          }}
        />
      )}
      {editing && (
        <JsonDialog
          api={api}
          homeId={homeId}
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {dialog}
    </>
  );
}

function Runs({ api, homeId, rule }: { api: string; homeId: string; rule: Rule }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['rule-runs', rule.rule_id], queryFn: () => rulesApi.runs(api, homeId, rule.rule_id), retry: false, refetchInterval: 10_000 });
  if (rule.placement === 'local') return <p className="small muted">{t('rules.runs_local')}</p>;
  return (
    <table className="grid small" data-testid="rule-runs">
      <thead>
        <tr>
          <th>{t('rules.run_time')}</th>
          <th>{t('rules.run_trigger')}</th>
          <th>{t('rules.run_status')}</th>
          <th>{t('rules.run_error')}</th>
        </tr>
      </thead>
      <tbody>
        {(q.data ?? []).map((x) => (
          <tr key={x.run_id} data-run={x.run_id}>
            <td>{stamp(x.created_at)}</td>
            <td className="mono">{x.trigger_ref.startsWith('manual') ? t('rules.trigger_manual') : x.trigger_ref}</td>
            <td>{t(`rules.status_${x.status}`, { defaultValue: t('common.state_unknown') })}</td>
            <td className={x.error ? 'error' : undefined}>
              {x.error
                ? t('rules.run_error_at', {
                    n: (x.error.action_index ?? -1) + 1,
                    reason: t(`rules.reason_${x.error.reason}`, { defaultValue: t('rules.reason_other') }),
                  })
                : '—'}
            </td>
          </tr>
        ))}
        {q.isSuccess && q.data.length === 0 && (
          <tr>
            <td colSpan={4} className="muted">
              {t('rules.runs_empty')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function TemplateDialog({
  catalog,
  initial,
  onClose,
  onCreate,
}: {
  catalog: { things: import('../../lib/rulesApi').CatalogThing[]; action_types: string[] };
  initial: TemplateId;
  onClose(): void;
  onCreate(spec: RuleSpec): Promise<void>;
}) {
  const { t } = useTranslation();
  const [id, setId] = useState<TemplateId>(initial);
  const [picks, setPicks] = useState<Record<string, string | undefined>>({});
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const notify = catalog.action_types.includes('notify');
  const cands = useMemo(() => candidates(id, catalog.things), [id, catalog.things]);
  const required = id === 'ai_person_notify' ? ['camera'] : ['sensor'];
  const spec = buildTemplate(id, name.trim() || t(`rules.tpl_${id}`), picks, catalog.things, notify, text.trim() || t(`rules.tpl_${id}_text`));
  // 範本必要的設備這個家戶沒有：〔建立〕停用並說明（第五輪 B5-17：原本寫了「還沒有」卻還能按）
  const lacking = required.some((r) => !(cands as Record<string, unknown[]>)[r]?.length);
  return (
    <Dialog open title={t('rules.from_template')} onClose={onClose} wide>
      <div className="seg" role="group" aria-label={t('rules.template')}>
        {TEMPLATES.map((x) => (
          <button key={x} aria-pressed={x === id} onClick={() => (setId(x), setPicks({}))}>
            {t(`rules.tpl_${x}`)}
          </button>
        ))}
      </div>
      <p className="muted small">{t(`rules.tpl_${id}_hint`)}</p>
      {Object.entries(cands).map(([role, list]) => (
        <label key={role} className="field">
          <span>
            {t(`rules.role_${role}`)}
            {!required.includes(role) && <span className="muted small"> {t('rules.optional')}</span>}
          </span>
          <select className="input" value={picks[role] ?? ''} onChange={(e) => setPicks({ ...picks, [role]: e.target.value || undefined })}>
            <option value="">{list.length ? t('rules.pick') : required.includes(role) ? t(`rules.tpl_${id}_needs`) : t('rules.none_capable')}</option>
            {list.map((x) => (
              <option key={x.device_ref} value={x.device_ref}>
                {refFullName(x.device_ref, x.display_name, catalog.things)}
              </option>
            ))}
          </select>
        </label>
      ))}
      <label className="field">
        <span>{t('rules.name')}</span>
        <input className="input" value={name} placeholder={t(`rules.tpl_${id}`)} onChange={(e) => setName(e.target.value)} />
      </label>
      {notify ? (
        <label className="field">
          <span>{t('rules.notify_text')}</span>
          <input className="input" maxLength={180} value={text} placeholder={t(`rules.tpl_${id}_text`)} onChange={(e) => setText(e.target.value)} />
        </label>
      ) : (
        <p className="notice small">{t('rules.notify_off')}</p>
      )}
      {err && <p className="error small">{t(`rules.err_${err}`, { defaultValue: t('common.error') })}</p>}
      {lacking && (
        <p className="small warn-text" data-testid="tpl-lacking">
          {t('rules.tpl_cannot_create', { what: t(`rules.tpl_${id}_needs`) })}
        </p>
      )}
      <div className="dialog-actions">
        <button className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={!spec || lacking}
          // 停用時滑過寫原因（第十三輪 B13-06）：家戶沒有必要的設備 / 必要的設備還沒選
          title={
            lacking
              ? t('rules.tpl_cannot_create', { what: t(`rules.tpl_${id}_needs`) })
              : !spec
                ? t('rules.tpl_pick_first', { what: required.map((r) => t(`rules.role_${r}`)).join(t('common.list_sep')) })
                : undefined
          }
          data-testid="tpl-create"
          onClick={() => {
            if (!spec) return;
            setErr(null);
            onCreate(spec).catch((e) => setErr(errCode(e)));
          }}
        >
          {t('rules.create')}
        </button>
      </div>
    </Dialog>
  );
}

function JsonDialog({ api, homeId, rule, onClose, onSaved }: { api: string; homeId: string; rule: Rule | null; onClose(): void; onSaved(): void }) {
  const { t } = useTranslation();
  const initial: RuleSpec = rule
    ? { kind: rule.kind, name: rule.name, enabled: rule.enabled, mode: rule.mode, triggers: rule.triggers, conditions: rule.conditions, actions: rule.actions }
    : { kind: 'automation', name: '', mode: 'single', triggers: [], conditions: [], actions: [] };
  const [text, setText] = useState(JSON.stringify(initial, null, 2));
  const [problems, setProblems] = useState<RuleProblem[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const parse = (): RuleSpec | null => {
    try {
      return JSON.parse(text) as RuleSpec;
    } catch {
      setMsg(t('rules.bad_json'));
      return null;
    }
  };
  const validate = async () => {
    setMsg(null);
    const spec = parse();
    if (!spec) return false;
    const r = await rulesApi
      .validate(api, homeId, spec)
      .catch((e) => ({ ok: false, problems: problemsOf(e) ?? [] }));
    setProblems(r?.problems ?? []);
    if (r?.ok) setMsg(t('rules.valid', { placement: t(`rules.placement_${(r as { placement?: string }).placement ?? 'cloud'}`) }));
    return !!r?.ok;
  };
  const save = async () => {
    if (!(await validate())) return;
    const spec = parse()!;
    try {
      if (rule) await rulesApi.put(api, homeId, rule.rule_id, { ...spec, version: rule.version });
      else await rulesApi.create(api, homeId, spec);
      onSaved();
    } catch (e) {
      const ps = problemsOf(e);
      if (ps) setProblems(ps);
      setMsg(errCode(e));
    }
  };
  return (
    <Dialog open title={rule ? t('rules.edit_title', { name: rule.name }) : t('rules.new_json')} onClose={onClose} wide>
      <p className="muted small">{t('rules.json_hint')}</p>
      <textarea className="input mono" rows={16} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('rules.json')} />
      {problems && problems.length > 0 && (
        <ul className="error small" data-testid="rule-problems">
          {problems.map((p, i) => (
            <li key={i}>
              <span className="mono">{p.path}</span>：{p.message || p.code}
            </li>
          ))}
        </ul>
      )}
      {msg && <p className="small muted">{msg}</p>}
      <div className="dialog-actions">
        {/* B-27：右上 × 之外也要有〔取消〕 */}
        <button className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button className="btn" onClick={() => void validate()}>
          {t('rules.validate')}
        </button>
        <button className="btn btn-primary" onClick={() => void save()}>
          {t('common.save')}
        </button>
      </div>
    </Dialog>
  );
}
